import { EarningsInsight } from '@api';
import { type AppTableFeatures } from '@design/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { CalendarEvents } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getCompanyColumns } from './events-table/columns/getCompanyColumns';
import { getEarningsColumns } from './events-table/columns/getEarningsColumns';
import { getEconomicsColumns } from './events-table/columns/getEconomicsColumns';
import { TabsType } from './utils';

interface UseEventsColumnsProps {
  activeTab: string;
  earningsInsightByEarningsId?: Map<string, EarningsInsight>;
  isEarningsInsightsFetching?: boolean;
}

export const useEventsColumns = ({
  activeTab,
  earningsInsightByEarningsId,
  isEarningsInsightsFetching = false,
}: UseEventsColumnsProps): ColumnDef<AppTableFeatures, CalendarEvents>[] => {
  const { t } = useTranslation(['calendar']);
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  return useMemo(() => {
    switch (activeTab) {
      case TabsType.Economics:
        return getEconomicsColumns() as ColumnDef<
          AppTableFeatures,
          CalendarEvents
        >[];
      case TabsType.Company:
        return getCompanyColumns() as ColumnDef<
          AppTableFeatures,
          CalendarEvents
        >[];
      default:
        return getEarningsColumns({
          t,
          isBasicOrLightUser,
          earningsInsightByEarningsId,
          isEarningsInsightsFetching,
        }) as ColumnDef<AppTableFeatures, CalendarEvents>[];
    }
  }, [
    activeTab,
    t,
    isBasicOrLightUser,
    earningsInsightByEarningsId,
    isEarningsInsightsFetching,
  ]);
};
